import { Drawer, Button, Text } from "rizzui";
import dayjs from "dayjs";
import {
  EPushNotificationStatus,
  IPushNotification,
} from "@/utilities/types/PushNotification";
import PushNotificationStatusCell from "./push-notification-table/status-cell";

export interface PushNotificationDetailDrawerProps {
  item: IPushNotification | null;
  onClose: () => void;
  onAfterChange?: () => void;
}

export default function PushNotificationDetailDrawer({
  item,
  onClose,
}: PushNotificationDetailDrawerProps) {
  return (
    <Drawer isOpen={Boolean(item)} onClose={onClose} size="lg">
      {item ? (
        <div className="flex h-full flex-col">
          <div className="border-b border-muted p-5">
            <div className="mb-1 flex items-center gap-2">
              <PushNotificationStatusCell status={item.status} />
              <Text className="text-xs text-gray-400">
                {dayjs(item.createdAt).format("MMM DD, YYYY HH:mm")}
              </Text>
            </div>
            <h3 className="text-lg font-bold">{item.title}</h3>
            <p className="mt-1 text-sm text-gray-600">{item.body}</p>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto p-5">
            {item.imageUrl ? (
              <Section title="Image">
                <img
                  src={item.imageUrl}
                  alt="Notification banner"
                  className="max-h-40 rounded-md border border-muted object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display =
                      "none";
                  }}
                />
              </Section>
            ) : null}

            <Section title="Payload">
              {item.deepLink ? (
                <Row label="Deep link" value={item.deepLink} />
              ) : (
                <Row label="Deep link" value="—" />
              )}
            </Section>

            <Section title="Schedule">
              {item.sentAt ? (
                <Row
                  label="Sent at"
                  value={dayjs(item.sentAt).format("MMM DD, YYYY HH:mm")}
                />
              ) : (
                <Row label="Sent at" value="—" />
              )}
              {item.scheduledAt ? (
                <Row
                  label="Scheduled at"
                  value={dayjs(item.scheduledAt).format("MMM DD, YYYY HH:mm")}
                />
              ) : (
                <Row label="Scheduled at" value="—" />
              )}
              {item.createdBy ? (
                <Row label="Created by" value={item.createdBy} />
              ) : null}
            </Section>

            {item.stats ? (
              <Section title="Delivery stats">
                <Row label="Sent" value={item.stats.sent.toLocaleString()} />
                <Row
                  label="Failed"
                  value={item.stats.failed.toLocaleString()}
                />
                <Row
                  label="Total deliveries"
                  value={item.stats.totalDeliveries.toLocaleString()}
                />
                <DeliveryProgressBar stats={item.stats} />
              </Section>
            ) : null}

            {item.status === EPushNotificationStatus.SENT ||
            item.status === EPushNotificationStatus.FAILED ? (
              <Section title="Activity">
                <Timeline item={item} />
              </Section>
            ) : null}
          </div>

          <div className="border-t border-muted p-4">
            <Button onClick={onClose} variant="outline" className="w-full">
              Close
            </Button>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
        {title}
      </h4>
      <div className="space-y-2 rounded-md border border-muted bg-gray-50/50 p-3">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-right font-medium text-gray-800">{value}</span>
    </div>
  );
}

function DeliveryProgressBar({
  stats,
}: {
  stats: NonNullable<IPushNotification["stats"]>;
}) {
  const total = Math.max(stats.totalDeliveries, 1);
  const sentPct = (stats.sent / total) * 100;
  const failedPct = (stats.failed / total) * 100;

  return (
    <div className="mt-1">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          style={{ width: `${sentPct}%` }}
          className="h-full bg-green-500"
          title="Sent"
        />
        <div
          style={{ width: `${failedPct}%` }}
          className="h-full bg-red-500"
          title="Failed"
        />
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-gray-500">
        <span>{sentPct.toFixed(0)}% sent</span>
        <span>{failedPct.toFixed(0)}% failed</span>
      </div>
    </div>
  );
}

function Timeline({ item }: { item: IPushNotification }) {
  const events: { label: string; at?: string | null }[] = [
    { label: "Created", at: item.createdAt },
    { label: "Scheduled", at: item.scheduledAt },
    { label: "Sent", at: item.sentAt },
  ];

  return (
    <ol className="space-y-2 text-sm">
      {events.map((ev) => (
        <li key={ev.label} className="flex items-center justify-between">
          <span className="text-gray-600">{ev.label}</span>
          <span className="text-gray-500">
            {ev.at ? dayjs(ev.at).format("MMM DD, YYYY HH:mm") : "—"}
          </span>
        </li>
      ))}
    </ol>
  );
}
